import { getBudget, useGetWorkPlan } from "@api/workplanService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import Alert from '@mui/joy/Alert';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, LinearProgress, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import { formatCurrency } from "@utils/numbers";
import { generateCompiledJobs, useGetOpportunity } from "@api/opportunityService";
import { CopyLinkButton } from "@components/CopyLinkButton";
import { ContextualHelp } from "@components/ContextualHelp";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { authenticatedApi } from "api";
import { queryClient } from "@api/queryClient";
import WarningDialog from '@components/dialogs/WarningDialog';


const CardHeader = () => {
    const data = useContext(ObjectContext);

    const totalJobShifts = data.jobs.reduce((count, job) => {
        return (job.quantity * job.available_time_slots.length) + count;
    }, 0);

    const assignedJobShifts = data.sign_ups.length;
    const progress = totalJobShifts ? Math.min(((assignedJobShifts / totalJobShifts) * 100), 100) : 0;

    return (
        <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            p={1.5}
            pt={0}
        >
            <Box sx={{flex: 1}}>
                <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>

                {totalJobShifts > 0 && (
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={2}
                >
                    <Box sx={{flex: 'auto', whiteSpace: 'nowrap'}}>
                        <Typography fontSize={'sm'}>{assignedJobShifts} of {totalJobShifts} filled</Typography>
                    </Box>
                    <Box sx={{flex: '1 1 100%', display: 'flex', justifyContent: 'flex-end'}}>
                        <LinearProgress determinate value={progress} sx={{backgroundColor: 'var(--joy-palette-background-backdrop)'}} />
                    </Box>
                </Stack>
                )}
            </Box>
        </Stack>
    )
};


const AttachVolunteerHours = (props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const data = useContext(ObjectContext) ?? {};
    const { id } = useParams();

    const hasSignUps = data.sign_ups.length > 0;

    const attachVolunteerHours = () => {
        setOpen(false);
        setLoading(true);

        const performUpdate = authenticatedApi.post(`/opportunities/${id}/attach-hours`, {});
        toast.promise(performUpdate, {
            loading: `Attaching...`,
            success: `The volunteer hours were attached!`,
            error: (err) => `There was an error while saving.`,
        });
        performUpdate.then((response) => {
            //setOpen(false);
            queryClient.invalidateQueries({ queryKey: ['opportunities'/*, id*/] });
            setLoading(false);
        });
    }

    // this should never occur
    if(data.volunteer_hours.length) {
        return null;
    }

    return (
        <>
            <WarningDialog
                title="Attach Volunteer Hours"
                content={<>Are you sure you want to attach volunteer hours?<br/>This action can only be performed once, so please wait until after your event.</>}
                confirmText="Add Hours"
                onConfirm={attachVolunteerHours}
                activateButton={(btnProps)=>
                    <Button
                        size="sm"
                        color="neutral"
                        variant="outlined"
                        sx={{ bgcolor: 'background.surface' }}
                        onClick={()=>setIsOpen(true)}
                        loading={loading}
                        disabled={!hasSignUps}
                        {...props}
                        {...btnProps}
                    >
                        Attach Volunteer Hours
                    </Button>
                }
            />

            <Alert
                color="warning"
                variant="soft"
                sx={{
                    mt: 1
                }}
            >
                {hasSignUps === true && (<Typography><b>Note:</b> You will only be able to press this button once, so please wait until after your event.</Typography>)}
                {hasSignUps !== true && (<Typography><b>Note:</b> You cannot attach volunteer hours because 0 slots are filled.</Typography>)}
            </Alert>
        </>
    );
}

const dataToDisplay/*: readonly ShowData[]*/ = [
    {
        label: 'Event',
        conditional: object => !!object.event,
        content: (object) => <ObjectPreview to={`/events/${object.event_id}`} object={object.event}>{object.event.name}</ObjectPreview>,
    },
    {
        label: 'Work Plan',
        conditional: object => !!object.workplan,
        content: (object) => <ObjectPreview to={`/work-plans/${object.workplan_id}`} object={object.workplan}>{object.workplan.name}</ObjectPreview>,
    },
    {
        label: <ContextualHelp slug="opportunity_link">Link</ContextualHelp>,
        // conditional: object => !!object.workplan,
        content: (object) => <CopyLinkButton link={object?.full_link}/>,
    },
    {
        label: 'Hours',
        conditional: (object, {user, can}) => !object.volunteer_hours.length && can('edit work plans'),
        content: (object) => <AttachVolunteerHours/>,
    },
    {
        label: 'Notes',
        conditional: object => !!object.notes,
        content: (object) => object.notes,
    },
];

export default function OpportunityShow() {
    return (
        <ObjectShow
            query={useGetOpportunity}
            type={'opportunity'}
            permissionType={'work plans'}
            title={'Opportunities'}
            subtitleKey={'name'}
            color={'default'}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfo
                    title={'Opportunity Info'}
                    tint={'default'}
                    fields={dataToDisplay}
                    cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'time_slots'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'notes'} column={'description'} title={'Description'} />
                <ObjectRelationship type={'files'} />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationship type={'jobs'} mutator={generateCompiledJobs} />
            </ObjectColumn>

        </ObjectShow>
    )
}
